import { t } from '@lingui/macro';
import React, { useState, useCallback, useMemo } from 'react';

import useDebounce from '@components/common/custom_hooks/useDebounce';
import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import { SuggestedNamesList } from '@components/people_tags/common/SuggestedNamesList';
import { TextSanitizationTooltip } from '@components/shared/sanitization';

import type { IdentifiedPerson, UnidentifiedPerson } from './types';

import { FaceThumbnail } from './FaceThumbnail';
import { SameNameDetectedModal } from './SameNameDetectedModal';

import Wrapper, {
  PeopleSection,
  SectionHeader,
  HeaderText,
  PeopleContent,
  PeopleTagsContainer,
  PersonTag,
  PersonImage,
  PersonName,
  PersonNameText,
  RemovePersonButton,
  CustomDropdown,
  DropdownHeader,
  SelectedOption,
  OptionImage,
  OptionText,
  DropdownIcon,
  InputStyled
} from './styles/PeopleInAssetsComponents';

// TODO: We have to remove this mock data once the backend is ready.
// Mock data for people options in dropdown
const peopleOptions = [
  {
    id: 1,
    name: 'Tayler Reed',
    image: 'https://example.com/placeholder.jpg' // Keep image for dropdown display
  },
  {
    id: 2,
    name: 'Alex Johnson',
    image: 'https://example.com/placeholder2.jpg',
  },
  { id: 3, name: 'Jamie Smith', image: 'https://example.com/placeholder3.jpg' },
  { id: 4, name: 'Casey Brown', image: 'https://example.com/placeholder4.jpg' },
];

// Define a type for the people options to avoid confusion with IdentifiedPerson
type PeopleOption = {
  id: number;
  name: string;
  image: string;
};

interface PeopleInAssetProps {
  identifiedPeople?: IdentifiedPerson[];
  unidentifiedPeople?: UnidentifiedPerson[];
  boundaryId?: string; // Added for API calls
}

// Mock data for demonstration purposes
// Note: All faces come from the same asset URL
const ASSET_URL = 'https://cdn.fs.brandfolder.com/Gvhj4cnuSm2pNVyk3XtB/convert?fit=max&format=jpeg&h=162&policy=eyJjYWxsIjpbImNvbnZlcnQiLCJyZWFkIl0sImV4cGlyeSI6MTc2MDk1NDAzOCwiaGFuZGxlIjoiR3ZoajRjbnVTbTJwTlZ5azNYdEIifQ%3D%3D&rotate=exif&signature=e058382cf27321bc3c80c561db480e377128c8d6efdcfd78387ce0426cf67a91&strip=false&w=262';

const mockIdentifiedPeople: IdentifiedPerson[] = [
  {
    id: 1,
    name: 'Barack Obama',
    assetUrl: ASSET_URL,
    boundingBox: {
      width: 0.17338751256465912,
      height: 0.34844306111335754,
      left: 0.3103182911872864,
      top: 0.11187176406383514
    }
  },
];

const mockUnidentifiedPeople: UnidentifiedPerson[] = [
  {
    id: 2,
    assetUrl: ASSET_URL,
    boundingBox: {
      width: 0.17338751256465912,
      height: 0.34844306111335754,
      left: 0.3103182911872864,
      top: 0.11187176406383514
    }
  },
];

function PeopleInAsset({
  identifiedPeople: propIdentifiedPeople = mockIdentifiedPeople,
  unidentifiedPeople: propUnidentifiedPeople = mockUnidentifiedPeople,
  boundaryId = 'mock-boundary-id', // Default for testing
}: PeopleInAssetProps): React.ReactElement {
  // State to manage our own lists of identified and unidentified people
  const [identifiedPeople, setIdentifiedPeople] =
    useState<IdentifiedPerson[]>(propIdentifiedPeople);
  const [unidentifiedPeople, setUnidentifiedPeople] = useState<
  UnidentifiedPerson[]
  >(propUnidentifiedPeople);

  // State to track selected person for each unidentified person
  const [selectedPeople, setSelectedPeople] = useState<
  Record<number, PeopleOption | null>
  >({});

  // State for dropdown open/close status
  const [openDropdowns, setOpenDropdowns] = useState<Record<number, boolean>>({});

  // State for search input for each unidentified person
  const [searchInputs, setSearchInputs] = useState<Record<number, string>>({});

  // Use debounce hook for search inputs
  const debouncedSearchInputs = useDebounce({
    delay: 300,
    value: searchInputs
  });

  // State for same name modal
  const [showSameNameModal, setShowSameNameModal] = useState(false);
  const [pendingPersonName, setPendingPersonName] = useState('');
  const [pendingPersonId, setPendingPersonId] = useState<number | null>(null);

  // State for error handling
  const [isLoading, setIsLoading] = useState<Record<number, boolean>>({});

  // Toggle dropdown open/close
  const toggleDropdown = useCallback((id: number): void => {
    if (isLoading[id]) return;
    setOpenDropdowns((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  }, [isLoading]);

  // Close dropdown
  const closeDropdown = useCallback((id: number): void => {
    setOpenDropdowns((prev) => ({
      ...prev,
      [id]: false
    }));
  }, []);

  // Handle search input change
  const handleSearchInputChange = useCallback((id: number, value: string): void => {
    setSearchInputs((prev) => ({
      ...prev,
      [id]: value
    }));

    // Open dropdown when user starts typing
    if (value.trim() !== '') {
      setOpenDropdowns((prev) => ({
        ...prev,
        [id]: true
      }));
    } else {
      // Close dropdown when input is empty
      closeDropdown(id);
    }
  }, [closeDropdown]);

  // Memoize filtered options for each person to prevent unnecessary filtering
  const memoizedFilteredOptions = useMemo(() => {
    const result: Record<number, typeof peopleOptions> = {};

    // Pre-compute filtered options for each unidentified person
    unidentifiedPeople.forEach((person) => {
      const id = person.id;
      const searchTerm = debouncedSearchInputs[id]?.toLowerCase() || '';

      if (!searchTerm) {
        result[id] = peopleOptions;
      } else {
        result[id] = peopleOptions.filter((option) =>
          option.name.toLowerCase().includes(searchTerm)
        );
      }
    });

    return result;
  }, [debouncedSearchInputs, unidentifiedPeople]);

  // Memoize the mapping function to convert options to suggestions
  const mapOptionsToSuggestions = useCallback((options: typeof peopleOptions) => {
    return options.map((option) => ({
      face_uri: option.image,
      person_id: String(option.id),
      tag_name: option.name
    }));
  }, []);

  // Memoize suggestions for each person
  const memoizedSuggestions = useMemo(() => {
    const result: Record<number, Array<{ face_uri: string; person_id: string; tag_name: string }>> = {};

    // Pre-compute suggestions for each unidentified person
    unidentifiedPeople.forEach((person) => {
      const id = person.id;
      const options = memoizedFilteredOptions[id] || peopleOptions;
      result[id] = mapOptionsToSuggestions(options);
    });

    return result;
  }, [memoizedFilteredOptions, unidentifiedPeople, mapOptionsToSuggestions]);

  // Get suggestions for a specific person
  const getSuggestionsForPerson = useCallback((id: number) => {
    return memoizedSuggestions[id] || mapOptionsToSuggestions(peopleOptions);
  }, [memoizedSuggestions, mapOptionsToSuggestions]);

  // Handle removing an identified person (moving to unidentified)
  const handleRemovePerson = useCallback((id: number): void => {
    // Set loading state
    setIsLoading((prev) => ({ ...prev, [id]: true }));

    // Find the identified person
    const identifiedPerson = identifiedPeople.find((p) => p.id === id);

    if (!identifiedPerson) {
      console.error(t`Person not found:`, id);
      return;
    }

    // Make the actual API call
    fetch(
      `/external_webhooks/people_tagging/api/v1/${boundaryId}/identities/${id}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        // Create a new unidentified person from the identified person
        const newUnidentifiedPerson: UnidentifiedPerson = {
          id: identifiedPerson.id,
          assetUrl: identifiedPerson.assetUrl,
          boundingBox: identifiedPerson.boundingBox,
        };

        // Remove from identified people
        setIdentifiedPeople((prev) => prev.filter((p) => p.id !== id));

        // Add to unidentified people
        setUnidentifiedPeople((prev) => [...prev, newUnidentifiedPerson]);

        // Clear loading state
        setIsLoading((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      })
      .catch((error) => {
        console.error(t`Error removing person identity:`, error);
        // Clear loading state
        setIsLoading((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      });
  }, [boundaryId, identifiedPeople]);

  // Handle identifying a person from dropdown
  const handleIdentifyPerson = useCallback((id: number, selectedId: number): void => {
    // Find the selected person from options and the unidentified person
    let selectedPerson = peopleOptions.find((p) => p.id === selectedId);
    const unidentifiedPerson = unidentifiedPeople.find((p) => p.id === id);

    // If selectedPerson is not found in peopleOptions, it might be a newly created person
    if (!selectedPerson && searchInputs[id]?.trim()) {
      // Create a new person object with the entered name
      selectedPerson = {
        id: selectedId,
        name: searchInputs[id].trim(),
        image: 'https://example.com/placeholder.jpg', // Use a default image
      };
    }

    // If no selection or unidentified person not found, just update selection state
    if (!selectedPerson || !unidentifiedPerson) {
      if (selectedPerson) {
        setSelectedPeople((prev) => ({
          ...prev,
          [id]: selectedPerson,
        }));
      }
      return;
    }

    // Set loading state and clear any previous errors
    setIsLoading((prev) => ({ ...prev, [id]: true }));

    // Make the API call to identify the person
    fetch(
      `/external_webhooks/people_tagging/api/v1/${boundaryId}/identities/${id}?name=${encodeURIComponent(
        selectedPerson.name
      )}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        return response.json();
      })
      .then(() => {
        // Create a new identified person object
        const newIdentifiedPerson: IdentifiedPerson = {
          id: unidentifiedPerson.id,
          name: selectedPerson.name,
          assetUrl: unidentifiedPerson.assetUrl, // Use the unidentified person's asset URL
          boundingBox: unidentifiedPerson.boundingBox // Use the unidentified person's bounding box
        };

        // Add to identified people list
        setIdentifiedPeople((prev) => [...prev, newIdentifiedPerson]);

        // Remove from unidentified people list
        setUnidentifiedPeople((prev) => prev.filter((p) => p.id !== id));

        // Clean up selected people state
        setSelectedPeople((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });

        // Clear loading state
        setIsLoading((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      })
      .catch((error) => {
        console.error(t`Error identifying person:`, error);
        // Clear loading state
        setIsLoading((prev) => {
          const newState = { ...prev };
          delete newState[id];
          return newState;
        });
      });
  }, [boundaryId, unidentifiedPeople, searchInputs]);

  // Create a new person with the given name
  const createNewPerson = useCallback((id: number, name: string): void => {
    // Create a new person object with the entered name
    const newPerson = {
      id: Date.now(), // Generate a unique ID
      name: name.trim(),
      image: 'https://example.com/placeholder.jpg', // Use a default image
    };

    // Identify the person with the new name
    handleIdentifyPerson(id, newPerson.id);

    // Clear search input after creation
    setSearchInputs((prev) => ({
      ...prev,
      [id]: ''
    }));

    // Close dropdown
    closeDropdown(id);
  }, [handleIdentifyPerson, closeDropdown]);

  // Handle creating a new person with the entered name
  const handleCreateNewPerson = useCallback((id: number, name: string): void => {
    if (!name.trim()) return;

    // Check if there's an existing person with the same name
    const existingPerson = peopleOptions.find((person) =>
      person.name.toLowerCase() === name.toLowerCase()
    );

    if (existingPerson) {
      // Show same name modal
      setPendingPersonName(name.trim());
      setPendingPersonId(id);
      setShowSameNameModal(true);
    } else {
      // Create a new person object with the entered name
      createNewPerson(id, name);
    }
  }, [createNewPerson]);

  // Handle canceling the same name modal - just close it without taking any action
  const handleUseExistingPerson = useCallback((): void => {
    // Just close the modal and reset state
    setShowSameNameModal(false);
    setPendingPersonName('');
    setPendingPersonId(null);
  }, []);

  // Handle creating a new person with the same name
  const handleCreateNewPersonWithSameName = useCallback((): void => {
    if (pendingPersonId !== null && pendingPersonName) {
      // Create a new person with the same name
      createNewPerson(pendingPersonId, pendingPersonName);
    }

    // Close modal
    setShowSameNameModal(false);
    setPendingPersonName('');
    setPendingPersonId(null);
  }, [pendingPersonId, pendingPersonName, createNewPerson]);

  return (
    <I18nProviderWrapper>
      <Wrapper>
      <PeopleSection>
        <SectionHeader>
          <HeaderText>{t`People in asset`}</HeaderText>
          <TextSanitizationTooltip
            margin
            tooltipText={t`
              Name the faces in this asset by selecting from existing people or typing a new name.
              If a face was misidentified, remove the name and enter the correct one —
              this change only affects the current asset.
            `}
          />
        </SectionHeader>
        <PeopleContent>
          <PeopleTagsContainer>
            {/* Render identified people with names and cross buttons */}
            {identifiedPeople.map((person) => (
              <PersonTag key={`identified-${person.id}`}>
                <PersonImage>
                  <FaceThumbnail
                     alt={person.name}
                     assetUrl={person.assetUrl}
                     boundingBox={person.boundingBox}
                     size={48}
                   />
                </PersonImage>
                <PersonName>
                  <PersonNameText>{person.name}</PersonNameText>
                  <RemovePersonButton
                    aria-label={t`Remove ${person.name}`}
                    disabled={isLoading[person.id]}
                    onClick={(): void => handleRemovePerson(person.id)}
                  >
                    {isLoading[person.id] ? (
                      <span className='icon bff-spinner' />
                    ) : (
                      <span className='icon bff-close' />
                    )}
                  </RemovePersonButton>
                </PersonName>
              </PersonTag>
            ))}

            {/* Render unidentified people with custom dropdown showing faces */}
            {unidentifiedPeople.map((person) => {
              const selectedPerson = selectedPeople[person.id];
              const isOpen = openDropdowns[person.id] || false;

              return (
                <PersonTag key={`unidentified-${person.id}`}>
                  <PersonImage>
                    {/* Show the selected person's image if available, otherwise show the
                        unidentified person's image */}
                    <FaceThumbnail
                      alt={t`Unidentified person`}
                      assetUrl={person.assetUrl}
                      boundingBox={person.boundingBox}
                      size={48}
                    />
                  </PersonImage>
                  <PersonName>
                    <CustomDropdown>
                      <DropdownHeader>
                        {selectedPerson ? (
                          <SelectedOption>
                            <OptionImage
                              alt={selectedPerson.name}
                              src={selectedPerson.image} // Using image from PeopleOption type
                            />
                            <OptionText>{selectedPerson.name}</OptionText>
                          </SelectedOption>
                        ) : (
                          <InputStyled
                            onChange={(e): void => handleSearchInputChange(person.id, e.target.value)}
                            placeholder={t`Name person`}
                            type="text"
                            value={searchInputs[person.id] || ''}
                          />
                        )}
                        <DropdownIcon onClick={(): void => toggleDropdown(person.id)}>
                          <span className="icon bff-caret-down" />
                        </DropdownIcon>
                      </DropdownHeader>

                      {isOpen && (
                          <SuggestedNamesList
                            handleCreateNewPerson={(name): void => {
                              handleCreateNewPerson(person.id, name);
                            }}
                            handleOnSelectTargetPerson={(selected): void => {
                              handleIdentifyPerson(person.id, Number(selected.person_id));
                              closeDropdown(person.id);
                              // Clear search input after selection
                              setSearchInputs((prev) => ({
                                ...prev,
                                [person.id]: ''
                              }));
                            }}
                            showCreateOption={!!searchInputs[person.id]?.trim()}
                            suggestions={getSuggestionsForPerson(person.id)}
                            typedName={searchInputs[person.id] || ''}
                          />
                      )}
                    </CustomDropdown>
                  </PersonName>
                </PersonTag>
              );
            })}
          </PeopleTagsContainer>
        </PeopleContent>
      </PeopleSection>
    </Wrapper>
      {/* Same Name Detected Modal */}
      <SameNameDetectedModal
        isOpen={showSameNameModal}
        onCancel={handleUseExistingPerson}
        onCreateNew={handleCreateNewPersonWithSameName}
      />
    </I18nProviderWrapper>
  );
}

export default PeopleInAsset;
